import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ABOAssignmentTable.module.scss';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import {
  getTrackingAwardIconFromCode,
  getAwardNameFromCode,
  getTrackingAwardFromCode,
  getTrackingAwardColorFromCode,
} from '../../../utils/awardIconMapping';
import { useTranslation } from 'react-i18next';
import Checkbox from '../../common/CustomCheckbox/CustomCheckbox';
import Spinner from '../../common/Spinner/Spinner';
import Icon from '../../common/Icon/Icon';
import BulkAssignmentView from '../BulkAssignmentView/BulkAssignmentView';
import {
  sortTableListData,
  useABOProfileNavigation,
  useProfileParams,
} from '../../../utils/common';
import { isEmpty, throttle } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastMessageType } from '../../../types/types';
import ToastMessage from '../../common/ToastMessage/ToastMessage';
import { FilterKey } from '../FilterDrawer/FilterContext/FilterContext';
import { getCountryNameFromCode } from '../../../utils/awardIconMapping';
import { useLocation } from 'react-router-dom';
import LanguageName from '../../common/LanguageName/LanguageName';
import { useSelector } from 'react-redux';
import { withUtag } from '../../../utils/analytics';

export interface AwardInfo {
  awardCode: string;
  awardName: string;
  awardTagCode?: number;
  awardTagName?: string;
}

export interface AssignmentData {
  aboInfo: string;
  aboNumber: string;
  affiliateCode: string;
  marketName: string;
  primaryAM: string;
  caseEvaluator: string;
  uplineEmerald: string;
  amEmerald: string;
  uplineDiamond: string;
  language: string;
  state: string;
  loa: string;
  currentAward: AwardInfo;
  trackingAward: AwardInfo;
}

interface AssignmentTableProps {
  data: any[];
  filterOrder: FilterKey[];
  isAllRecordsFetched: boolean;
  isForAssignment: boolean | null;
  onAssignCallback: () => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

type VarColumnConfig = {
  columnName: string;
  path: string;
  className: string;
  render: (item: any) => React.ReactNode;
};

const tblHeaders = [{ columnName: 'ABO Info', path: 'aboNumber', className: styles.abo_info_cell }];

const ABOAssignmentTable: React.FC<AssignmentTableProps> = ({
  data,
  filterOrder,
  isAllRecordsFetched,
  isForAssignment,
  onAssignCallback,
  onLoadMore,
  isLoadingMore,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isExpanded = useAppSelector((state) => state.sideNavExpansion.isExpanded);
  const currentTheme = useAppSelector((state: RootState) => state.theme.currentTheme);
  const tableContainerRef = useRef<HTMLInputElement>(null);
  const [tableDataList, setTableDataList] = useState<any[]>(data);
  const [displayData, setDisplayData] = useState<any[]>([]);
  const [searchParams] = useSearchParams();
  const { aboNumber } = useProfileParams();
  const preSelectedAbo = aboNumber;
  const location = useLocation();
  const [selectedABOs, setSelectedABOs] = useState<
    Array<{ aboNumber: string; affiliateCode: string }>
  >([]);
  const selectedLang = useSelector((state: any) => state.language.currentLanguage);

  const userDetails = useAppSelector((state: RootState) => state.userDetails.user);
  const [userCountryCode] = useState(userDetails?.userCountryCode);

  useEffect(() => {
    if (isForAssignment && preSelectedAbo && data?.length > 0) {
      const matchingAbo = data.find((abo) => abo?.aboNumber === String(preSelectedAbo));

      if (matchingAbo) {
        setSelectedABOs([
          {
            aboNumber: matchingAbo.aboNumber,
            affiliateCode: matchingAbo.affiliateCode,
          },
        ]);
      }
    }
  }, [preSelectedAbo, data]);

  useEffect(() => {
    if (location.pathname === '/assignment') {
      setSelectedABOs([]);
    }
  }, [location.pathname]);

  useEffect(() => {
    const viewUtagObject = {
      event_name: 'aboassignment_view',
      am_country: localStorage?.getItem('userCountryCode') || '',
      am_affiliate: localStorage?.getItem('userAffiliateCode') || '',
      user_role: JSON.parse(localStorage.getItem('userRoleType') || '[]'),
      user_type: localStorage?.getItem('userType') || '',
      theme_toggle: currentTheme,
    };

    withUtag().then((utag) => utag.view(viewUtagObject));
  }, []);

  const variableTblConfig = useMemo<Partial<Record<FilterKey, VarColumnConfig>>>(
    () => ({
      trackingAward: {
        columnName: 'Tracking / Current Award',
        path: 'trackingAward.awardName',
        className: styles.tracking_award_cell,
        render: (item: any) => (
          <div className={styles.cell_data}>
            <div className={styles.award_wrapper}>
              <span className={styles.award_name}>
                {getAwardNameFromCode(item?.trackingAward?.awardCode, t)}
              </span>
              {
                <div
                  className={`award_icon ${styles.awardIcon}`}
                  style={{
                    backgroundColor: currentTheme === 'light' ? '#F4F4F4' : '#1C1C1E',
                  }}
                >
                  <span className={`award_icon_left ${styles.awardIconLeft}`}>
                    {getTrackingAwardIconFromCode(item?.trackingAward?.awardTagCode, currentTheme)}
                  </span>
                  <span
                    className={`award_name ${styles.awardIconRight}`}
                    style={{
                      color: getTrackingAwardColorFromCode(
                        item?.trackingAward?.awardTagCode,
                        currentTheme
                      ),
                    }}
                  >
                    {getTrackingAwardFromCode(item?.trackingAward?.awardTagCode, t)}
                  </span>
                </div>
              }
            </div>
            <div className={styles.award_wrapper}>
              <span className={styles.award_name_no_ellipsis}>
                {getAwardNameFromCode(item?.currentAward?.awardCode, t)}
              </span>
            </div>
          </div>
        ),
      },
      primaryAM: {
        columnName: 'Primary AM',
        path: 'primaryAccountManagerFullName',
        className: styles.med_col,
        render: (item) => (
          <span className={styles.clamped_text}>
            {item?.primaryAccountManagerFullName
              ? (item?.primaryAccountManagerFullName ?? '')
              : '--'}
          </span>
        ),
      },
      caseEvaluator: {
        columnName: 'Case Evaluator',
        path: 'caseEvaluatorFullName',
        className: styles.med_col,
        render: (item) => (
          <span className={styles.clamped_text}>
            {item?.caseEvaluatorFullName ? (item?.caseEvaluatorFullName ?? '') : '--'}
          </span>
        ),
      },
      uplineEmerald: {
        columnName: 'Upline Emerald',
        path: 'uplineEmerald.name',
        className: styles.med_col,
        render: (item) => <span className={styles.clamped_text}>{item?.uplineEmerald?.name}</span>,
      },
      amEmerald: {
        columnName: 'AM of Emerald',
        path: 'uplineEmerald.primaryAccountManagerFullName',
        className: styles.med_col,
        render: (item) => (
          <span className={styles.clamped_text}>
            {item?.uplineEmerald?.primaryAccountManagerFullName
              ? (item?.uplineEmerald?.primaryAccountManagerFullName ?? '')
              : '--'}
          </span>
        ),
      },
      uplineDiamond: {
        columnName: 'Upline Diamond',
        path: 'uplineDiamond.name',
        className: styles.med_col,
        render: (item) => <span className={styles.clamped_text}>{item?.uplineDiamond?.name}</span>,
      },
      language: {
        columnName: 'Language',
        path: 'language',
        className: styles.small_col,
        render: (item) => <LanguageName code={item?.language} locale={selectedLang} />,
      },
      state: {
        columnName: 'State',
        path: 'stateCode',
        className: styles.small_col,
        render: (item) => item?.stateCode,
      },
      ...(userCountryCode !== 'IN' && {
        loa: {
          columnName: 'LOA',
          path: 'loaName',
          className: styles.small_col,
          render: (item) => <span className={styles.clamped_text}>{item?.loaName}</span>,
        },
      }),
    }),
    [currentTheme, t]
  );

  const variableTblHeaders = filterOrder
    .map((filterKey) => variableTblConfig[filterKey])
    .filter((v): v is VarColumnConfig => Boolean(v));

  const handleAccountSelection = (abo: AssignmentData) => {
    setSelectedABOs((prevSelected) => {
      if (prevSelected.find((item) => item.aboNumber === abo.aboNumber)) {
        return prevSelected.filter((item) => item.aboNumber !== abo.aboNumber);
      } else {
        return [
          ...prevSelected,
          {
            aboNumber: abo.aboNumber,
            affiliateCode: abo.affiliateCode,
          },
        ];
      }
    });
    // navigate("/assignment", { replace: true });
  };

  const [toastConfig, setToastConfig] = useState({
    message: '',
    type: 'success' as toastMessageType,
    show: false,
  });

  const isAboInData = data.some((item) => item.aboNumber === String(preSelectedAbo));

  useEffect(() => {
    if (preSelectedAbo && data.length > 0 && !isAboInData) {
      setToastConfig({
        type: 'error',
        message: `ABO number [${preSelectedAbo}] is not present.`,
        show: true,
      });
    }
  }, [preSelectedAbo, isAboInData]);

  // const handleAllAccountSelection = (aboNumber: string) => {
  //   const allAbos = data.map((data) => data.aboNumber);
  //   const allSelectedABOs = allAbos.includes(aboNumber);
  //   if (allSelectedABOs) {
  //     setSelectedABOs([]);
  //   } else {
  //     setSelectedABOs(allAbos);
  //   }
  // };

  const handleSelectAll = () => {
    const isAllSelected = data.every(
      (abo) => selectedABOs.findIndex((item) => item.aboNumber === abo.aboNumber) !== -1
    );

    if (isAllSelected) {
      // Deselect all
      setSelectedABOs([]);
    } else {
      // Select all
      setSelectedABOs(
        data.map((item) => ({
          aboNumber: item.aboNumber,
          affiliateCode: item.affiliateCode,
        }))
      );
    }
    navigate('/assignment', { replace: true });
  };
  const selectedSortackgroundColor = currentTheme === 'light' ? '#CACACA' : '#494949';

  const [sortConfig, setSortConfig] = useState<{
    path: string;
    order: SortOrder;
  }>({
    path: '',
    order: 'nosort',
  });

  useEffect(() => {
    setTableDataList(data);
    setDisplayData(data);
  }, [data]);

  const requestMoreData = () => {
    if (!isAllRecordsFetched && onLoadMore && !isLoadingMore) {
      onLoadMore();
    }
  };

  const renderSortIcon = (path: string) => {
    if (sortConfig.path !== path || sortConfig.order === 'nosort') {
      if (isAllRecordsFetched) {
        return (
          <Icon
            name="arrows_up_down"
            size={16}
            className={styles.sort_icon}
            color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
          />
        );
      } else {
        return null;
      }
    }

    if (sortConfig.order === 'asc') {
      return (
        <Icon
          name="arrows_up"
          size={14}
          className={styles.sort_icon}
          color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
        />
      );
    }

    if (sortConfig.order === 'desc') {
      return (
        <Icon
          name="arrows_down"
          size={14}
          className={styles.sort_icon}
          color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
        />
      );
    }

    return null;
  };
  const sortedData: any[] = sortTableListData(displayData, sortConfig.path, sortConfig.order);
  const handleSort = (path: string) => {
    setSortConfig((prev) => {
      if (prev.path === path) {
        const nextOrder =
          prev.order === 'nosort' ? 'asc' : prev.order === 'asc' ? 'desc' : 'nosort';

        return { path, order: nextOrder };
      }
      return { path, order: 'asc' };
    });
  };
  const handleABOInfoClick = useABOProfileNavigation();
  const handleScroll = throttle(() => {
    const container = tableContainerRef.current;
    if (container) {
      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollHeight = container.scrollHeight;
      if (scrollHeight <= container.clientHeight) {
        return;
      }
      if (scrollPosition >= scrollHeight || scrollPosition >= scrollHeight - 10) {
        requestMoreData();
      }
    }
  }, 100);

  useEffect(() => {
    const container = tableContainerRef.current;
    container && container.addEventListener('scroll', handleScroll);
    return () => {
      container && container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <>
      <BulkAssignmentView
        selectedABOs={selectedABOs}
        resetSelection={() => setSelectedABOs([])}
        onAssignCallback={onAssignCallback}
      />
      {isEmpty(tableDataList) ? (
        <div className={styles.empty_state_document}>
          <div className={`table_no_data ${styles.empty_state_msg}`}>{t('noAboAvailable')}</div>
        </div>
      ) : (
        <div className={`hatv_table_container assignment_container ${styles.assignment_container}`}>
          <div
            className={`${styles.abo_table} ${isExpanded ? styles.expanded_width : ''}`}
            ref={tableContainerRef}
          >
            <div className={`abo_table_header ${styles.table_row_data} ${styles.abo_table_header}`}>
              {[...tblHeaders, ...variableTblHeaders].map((config, index) => {
                const { columnName, path } = config;
                const isActiveSort = sortConfig.path === path && sortConfig.order !== 'nosort';
                return (
                  <div
                    key={path}
                    className={`tbl_head_cell ${styles.tbl_head_cell} ${config.className ?? ''}`}
                    style={{
                      backgroundColor: isActiveSort ? selectedSortackgroundColor : '',
                    }}
                  >
                    <div
                      className={`${styles.flex_block}`}
                      style={{ justifyContent: 'flex-start', width: '100%' }}
                    >
                      {index === 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            width: '100%',
                          }}
                        >
                          <div className={styles.checkbox_wrapper}>
                            <Checkbox
                              checked={
                                data.length > 0 &&
                                data.every(
                                  (item) =>
                                    selectedABOs.findIndex(
                                      (abo) => abo.aboNumber === item.aboNumber
                                    ) !== -1
                                )
                              }
                              checkboxHandler={handleSelectAll}
                              isDisabled={data.length === 0}
                            />
                          </div>
                          <div className={styles.header_item}>{columnName}</div>
                        </div>
                      ) : (
                        <div className={styles.header_item}>{columnName}</div>
                      )}
                      <div className={styles.sort_icon} onClick={() => handleSort(path)}>
                        {renderSortIcon(path)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {sortedData.map((item: any, rowIndex: number) => (
              <div key={rowIndex} className={styles.inner_row_wrapper}>
                <div
                  className={`table_row_data ${styles.table_row_data}`}
                  data-testid="abo-assignment-table-row"
                >
                  <div
                    className={`tbl_row_data_col ${styles.tbl_row_data_col} ${styles.abo_info_cell}`}
                  >
                    <div className={`${styles.flex_block}`}>
                      <div className={styles.checkbox_wrapper}>
                        <Checkbox
                          checked={
                            selectedABOs.findIndex((abo) => abo.aboNumber === item.aboNumber) !== -1
                          }
                          checkboxHandler={() => handleAccountSelection(item)}
                          isDisabled={false}
                        />
                      </div>
                      <div
                        className={`${styles.data_cell} `}
                        onClick={() =>
                          handleABOInfoClick(item.aboNumber, item.affiliateCode, 'hatvqmonth')
                        }
                      >
                        <div className={`abo_name ${styles.abo_name}`}>{item.name}</div>
                        <div className={styles.abo_info}>
                          <div className={`abo_num ${styles.abo_num}`}>{item.aboNumber}</div>
                          <div className={`abo_num ${styles.abo_num}`}>
                            {getCountryNameFromCode(item.marketCountryCode, t)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {filterOrder.map((filterKey) => {
                    const config = variableTblConfig[filterKey];
                    if (!config) return null;
                    return (
                      <div
                        className={`tbl_row_data_col ${styles.tbl_row_data_col} ${config.className}`}
                        key={String(filterKey)}
                      >
                        {config.render(item)}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {isLoadingMore && (
              <div className={styles.spinner_styling}>
                <Spinner loadingSize="large" />
              </div>
            )}
          </div>
        </div>
      )}
      <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={(show) => setToastConfig((prev) => ({ ...prev, show }))}
        customStyles={{
          top: '96px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
        autoDismissal
      />
    </>
  );
};

export default ABOAssignmentTable;
